import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Archive, ArrowRight, CircleAlert, CircleCheck, Info, ListPlus, LoaderCircle } from 'lucide-react'

import { SectionLabel } from '@/components/px/SectionLabel'
import { Button } from '@/components/ui/button'
import { BOARD_FOCUS_RING } from '@/features/board/focusRing'
import { useProjectVersions } from '@/features/versions/hooks/useProjectVersions'
import {
  fetchVersions,
  selectActiveVersionFor,
  selectVersionsListErrorFor,
  versionsRetryArmed
} from '@/features/versions/slices/versionsSlice'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  generateTasks,
  generationClosed,
  selectCanGenerateTasks,
  selectGenerationFor,
  selectGenerationVersionsRead
} from '../slices/ticketGenerationSlice'

export interface GenerateTasksPanelProps {
  orgId: string
  projectId: string
  /** Outer spacing from the page — the card renders nothing at all when the caller may not generate. */
  className?: string
}

/**
 * "Generate tasks" on the requirements register (CR-BUILD-BOARD-EPICS-2026-09-26,
 * AC-19): its own card between the page header and the register. It asks the
 * server to draft epics / stories / tasks from the ACTIVE version's confirmed
 * requirements (`generate-tickets`, SSE, never `force`), shows the stream
 * live and ends in a summary or an inline refusal. The generated rows land in
 * the Build board's Backlog; the server skips requirements that already have
 * tasks (R.6.6), so a re-run never duplicates.
 *
 * Absent — never disabled — unless the caller is a firm-side writer
 * (INV-R.7, `selectCanGenerateTasks`). Failures stay in this card (INV-R.9);
 * unmounting or switching project aborts the run and forgets it (INV-R.8).
 *
 * Fix cycle 1: the versions read has its own loading line and inline error
 * with Retry (M2) — "no version" is said only when that read SUCCEEDED empty;
 * after "Yes, generate" focus moves to the status region (M5, WCAG 2.4.3);
 * the summary names the stale proposals the run archived (M4).
 */
export function GenerateTasksPanel({ orgId, projectId, className }: GenerateTasksPanelProps) {
  const { t } = useTranslation(['projectWorkspace', 'common'])
  const dispatch = useAppDispatch()
  const headingId = useId()
  const canGenerate = useAppSelector(selectCanGenerateTasks)
  // The keyed versions read the register's version chips already issue — no second request.
  useProjectVersions(orgId, projectId)
  const activeVersion = useAppSelector((s) => selectActiveVersionFor(s, projectId))
  const versionsRead = useAppSelector((s) => selectGenerationVersionsRead(s, projectId))
  const versionsError = useAppSelector((s) => selectVersionsListErrorFor(s, projectId))
  const generation = useAppSelector((s) => selectGenerationFor(s, projectId))
  // Keyed by project, like BulkImportPanel's confirm: a confirm opened on A never reappears on B.
  const [confirmingFor, setConfirmingFor] = useState<string | null>(null)
  const confirming = confirmingFor === projectId
  // Cancel hands focus back to the trigger, which re-mounts when the confirm row closes.
  const [focusTrigger, setFocusTrigger] = useState(false)
  const running = useRef<{ abort: () => void } | null>(null)
  // M5: each confirmed run moves focus to the status region once it renders.
  const statusRef = useRef<HTMLDivElement>(null)
  const [confirmedRuns, setConfirmedRuns] = useState(0)

  useEffect(() => {
    if (confirmedRuns > 0) statusRef.current?.focus()
  }, [confirmedRuns])

  useEffect(() => {
    const run = running
    return () => {
      run.current?.abort()
      run.current = null
      dispatch(generationClosed())
    }
  }, [dispatch, orgId, projectId])

  const onTriggerClicked = useCallback(() => {
    setFocusTrigger(false)
    setConfirmingFor(projectId)
  }, [projectId])

  const onCancelClicked = useCallback(() => {
    setConfirmingFor(null)
    setFocusTrigger(true)
  }, [])

  const onConfirmClicked = useCallback(() => {
    if (activeVersion === null) return
    running.current = dispatch(generateTasks({ orgId, projectId, versionSlug: activeVersion.id }))
    setConfirmingFor(null)
    setConfirmedRuns((count) => count + 1)
  }, [activeVersion, dispatch, orgId, projectId])

  // M2: an explicit re-arm first — a bare re-dispatch after a failed read is
  // vetoed by the cache (memory px-retry-needs-an-explicit-invalidate).
  const onVersionsRetryClicked = useCallback(() => {
    dispatch(versionsRetryArmed())
    void dispatch(fetchVersions({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  if (!canGenerate) return null

  const version = activeVersion?.name ?? ''
  const phase = generation?.phase ?? 'idle'
  const { epics = [], summary = null, failure = null, progressTicks = 0 } = generation ?? {}
  // Dotted server codes cannot be flat i18next keys (the default key separator
  // is `.`), so a code is looked up with `.` → `_`; an unknown code falls back
  // to the generic copy, and the raw code is always shown verbatim below.
  const errorKey = failure === null ? 'generic' : failure.code.split('.').join('_')
  const lede = t(`projectWorkspace:requirements.generate.errors.${errorKey}.lede`, {
    defaultValue: t('projectWorkspace:requirements.generate.errors.generic.lede')
  })
  const nextStep = t(`projectWorkspace:requirements.generate.errors.${errorKey}.next`, {
    defaultValue: t('projectWorkspace:requirements.generate.errors.generic.next')
  })

  return (
    <section
      aria-labelledby={headingId}
      data-testid="generate-tasks-panel"
      className={cn('rounded-lg border border-border bg-card p-4', className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <SectionLabel>{t('projectWorkspace:requirements.generate.label')}</SectionLabel>
          <h2 id={headingId} className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {t('projectWorkspace:requirements.generate.heading')}
          </h2>
          {activeVersion !== null && (
            <p className="mt-1.5 max-w-[44rem] text-[0.8125rem] text-ink-soft">
              {t('projectWorkspace:requirements.generate.body', { version })}
            </p>
          )}
        </div>
        {activeVersion !== null && !confirming && (
          <Button
            variant="accent"
            disabled={phase === 'streaming'}
            onClick={onTriggerClicked}
            autoFocus={focusTrigger}
            data-testid="generate-trigger"
          >
            <ListPlus aria-hidden className="size-4" />
            {t('projectWorkspace:requirements.generate.action', { version })}
          </Button>
        )}
      </div>

      {activeVersion === null && versionsRead === 'loading' && (
        <p
          role="status"
          className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"
          data-testid="generate-versions-loading"
        >
          <LoaderCircle aria-hidden className="size-4 animate-spin" />
          {t('projectWorkspace:requirements.generate.versionsLoading')}
        </p>
      )}

      {activeVersion === null && versionsRead === 'failed' && (
        <div role="alert" data-testid="generate-versions-error" className="mt-3 rounded-md border border-border bg-card px-3 py-2">
          <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-blocked">
            <CircleAlert aria-hidden className="size-4 shrink-0" />
            {t('projectWorkspace:requirements.generate.versionsError')}
          </p>
          <div className="ml-6 mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Button
              size="sm"
              variant="outline"
              onClick={onVersionsRetryClicked}
              data-testid="generate-versions-retry"
              className={BOARD_FOCUS_RING}
            >
              {t('common:states.retry')}
            </Button>
            {versionsError?.key !== undefined && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                {t('common:states.codeLabel')}
                <span className="font-mono text-foreground">{versionsError.key}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {activeVersion === null && versionsRead === 'loaded' && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="generate-no-version">
          {t('projectWorkspace:requirements.generate.noVersion')}
        </p>
      )}

      {activeVersion !== null && confirming && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-[0.8125rem] text-foreground">
            {t('projectWorkspace:requirements.generate.confirmPrompt', { version })}
          </p>
          <Button size="sm" autoFocus onClick={onConfirmClicked} data-testid="generate-confirm">
            {t('projectWorkspace:requirements.generate.confirm')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancelClicked} data-testid="generate-cancel">
            {t('projectWorkspace:requirements.generate.cancel')}
          </Button>
        </div>
      )}

      {generation !== null && phase !== 'idle' && (
        <div className="mt-3 border-t border-border pt-3">
          <div
            ref={statusRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            data-testid="generate-status"
            data-phase={phase}
            className={cn('rounded-sm', BOARD_FOCUS_RING)}
          >
            {phase === 'streaming' && (
              <p className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-foreground">
                <LoaderCircle aria-hidden className="size-4 animate-spin" />
                {t('projectWorkspace:requirements.generate.streaming', { version })}
                <span className="v-figure text-muted-foreground" data-testid="generate-ticks">
                  {t('projectWorkspace:requirements.generate.ticks', { count: progressTicks })}
                </span>
              </p>
            )}
            {phase === 'completed' && summary !== null && summary.count > 0 && (
              <>
                <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-foreground">
                  <CircleCheck aria-hidden className="size-4 text-done" />
                  {t('projectWorkspace:requirements.generate.finished')}
                </p>
                <dl className="mt-2.5 grid grid-cols-3 gap-2 text-center" data-testid="generate-summary">
                  <SummaryFigure label={t('projectWorkspace:requirements.generate.created')} value={summary.count} />
                  <SummaryFigure label={t('projectWorkspace:requirements.generate.used')} value={summary.requirementCount} />
                  <SummaryFigure
                    label={t('projectWorkspace:requirements.generate.skipped')}
                    value={summary.skippedRequirementCount}
                  />
                </dl>
              </>
            )}
            {phase === 'completed' &&
              summary !== null &&
              summary.count === 0 &&
              (summary.skippedRequirementCount > 0 || summary.requirementCount > 0) && (
                <p className="flex items-center gap-2 text-[0.8125rem] text-muted-foreground" data-testid="generate-nothing-new">
                  <Info aria-hidden className="size-4 shrink-0" />
                  {t('projectWorkspace:requirements.generate.nothingNew', {
                    count: summary.skippedRequirementCount > 0 ? summary.skippedRequirementCount : summary.requirementCount
                  })}
                </p>
              )}
            {phase === 'completed' && summary !== null && summary.archivedStaleCount > 0 && (
              <p
                className="mt-2 flex items-center gap-2 text-[0.8125rem] text-muted-foreground"
                data-testid="generate-archived-stale"
                data-count={summary.archivedStaleCount}
              >
                <Archive aria-hidden className="size-4 shrink-0" />
                {t('projectWorkspace:requirements.generate.archivedStale', { count: summary.archivedStaleCount })}
              </p>
            )}
          </div>

          {epics.length > 0 && (
            <ol aria-label={t('projectWorkspace:requirements.generate.created')} className="mt-2 flex flex-col gap-1">
              {epics.map((epic, index) => (
                <li key={`${epic.epicId}-${index}`} className="flex items-center gap-2 text-[0.8125rem] text-foreground">
                  <CircleCheck aria-hidden className="size-4 shrink-0 text-done" />
                  <span className="[overflow-wrap:anywhere]">{epic.title}</span>
                  <span className="text-muted-foreground">
                    {t('projectWorkspace:requirements.generate.epicLine', { count: epic.storyCount })}
                  </span>
                </li>
              ))}
            </ol>
          )}

          {phase === 'completed' && summary !== null && summary.count > 0 && (
            <p className="mt-2 text-[0.8125rem] text-foreground">
              {t('projectWorkspace:requirements.generate.landed')}{' '}
              <Link
                to={`/project/${projectId}/build`}
                data-testid="generate-open-board"
                className="inline-flex items-center gap-1 font-medium text-accent-foreground hover:underline"
              >
                {t('projectWorkspace:requirements.generate.openBoard')}
                <ArrowRight aria-hidden className="size-4" />
              </Link>
            </p>
          )}

          {phase === 'failed' && failure !== null && (
            <div role="alert" data-testid="generate-error" className="mt-3 rounded-md border border-border bg-card px-3 py-2">
              <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-blocked">
                <CircleAlert aria-hidden className="size-4 shrink-0" />
                {lede}
              </p>
              {epics.length > 0 && (
                <p className="ml-6 mt-0.5 text-xs text-ink-soft">
                  {t('projectWorkspace:requirements.generate.partial', { count: epics.length })}
                </p>
              )}
              <p className="ml-6 mt-0.5 text-xs text-ink-soft">{nextStep}</p>
              <dl className="ml-6 mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {failure.status !== undefined && (
                  <div className="flex items-center gap-1">
                    <dt>{t('common:states.statusLabel')}</dt>
                    <dd className="v-figure tabular-nums text-foreground" data-testid="generate-error-status">
                      {failure.status}
                    </dd>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <dt>{t('common:states.codeLabel')}</dt>
                  <dd className="font-mono text-foreground" data-testid="generate-error-code">
                    {failure.code}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

interface SummaryFigureProps {
  label: string
  value: number
}

function SummaryFigure({ label, value }: SummaryFigureProps) {
  return (
    <div>
      <dt className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="v-figure text-lg font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  )
}
